import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, MessageCircle, Clock, MapPin, Shield, Star, CheckCircle, Car, Home, Building2, Key, Zap, BadgePercent, HeartHandshake, ChevronDown, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const PHONE_NUMBER = "249-979-4550";
const PHONE_LINK = "tel:2499794550";
const WHATSAPP_LINK = "https://wa.me/12499794550?text=Hi,%20I%20need%20emergency%20locksmith%20service!";
const EMAIL = "info.nickelcitylocksmith@gmail.com";
const BUSINESS_NAME = "My Sudbury Locksmith";

// Google Ads conversion tracking - tracks then allows default link behavior
const trackCallConversion = (e) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'ads_conversion_Contact_1', {
      'event_callback': function() {},
      'event_timeout': 2000
    });
  }
  // Don't prevent default - let the tel: link work naturally
};

const trackWhatsAppConversion = (e) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'ads_conversion_Contact_1', {
      'event_callback': function() {},
      'event_timeout': 2000
    });
  }
  // Don't prevent default - let the link work naturally
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Hero Section
const HeroSection = () => (
  <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
    {/* Background Image */}
    <div className="absolute inset-0">
      <img 
        src="https://images.unsplash.com/photo-1616821086001-4f44ec8b1741?crop=entropy&cs=srgb&fm=jpg&q=85" 
        alt="Locksmith background" 
        className="w-full h-full object-cover"
      />
      <div className="hero-overlay absolute inset-0" />
    </div>
    
    {/* Content */}
    <div className="relative z-10 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto w-full py-32">
      {/* 24/7 Badge */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="inline-flex items-center gap-2 bg-[#111111]/80 backdrop-blur-md px-4 py-2 rounded-full border border-white/10 mb-8"
      >
        <Clock className="w-4 h-4 text-[#FACC15]" />
        <span className="text-white font-semibold">24/7 Emergency Service</span>
        <span className="text-[#A1A1AA]">•</span>
        <span className="text-[#FACC15]">Sudbury, ON</span>
      </motion.div>
      
      {/* Main Headline */}
      <motion.h1 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="font-heading text-6xl md:text-8xl font-black tracking-tighter uppercase leading-none text-white mb-4"
      >
        LOCKED OUT?
      </motion.h1>
      
      <motion.p 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-[#FACC15] mb-6"
      >
        WE'RE ON OUR WAY.
      </motion.p>
      
      <motion.p 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="text-lg md:text-xl text-[#A1A1AA] max-w-xl mb-10"
      >
        24/7 Emergency Locksmith Service in Sudbury, Ontario & Surrounding Areas. Fast Response. Fair Prices. Guaranteed.
      </motion.p>
      
      {/* CTA Buttons */}
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="flex flex-col sm:flex-row gap-4"
      >
        <Button 
          asChild
          data-testid="hero-call-btn"
          className="bg-[#FACC15] hover:bg-[#EAB308] text-black font-bold text-lg px-8 py-6 rounded-none glow-yellow glow-yellow-hover"
        >
          <a href={PHONE_LINK} onClick={trackCallConversion} className="flex items-center">
            <Phone className="w-5 h-5 mr-2" />
            CALL NOW - 24/7
          </a>
        </Button>
        <Button 
          asChild
          data-testid="hero-whatsapp-btn"
          variant="outline" 
          className="bg-white/10 hover:bg-white/20 text-white border-white/20 font-semibold text-lg px-8 py-6 rounded-none backdrop-blur-md"
        >
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" onClick={trackWhatsAppConversion} className="flex items-center">
            <MessageCircle className="w-5 h-5 mr-2" />
            WhatsApp Us
          </a>
        </Button>
      </motion.div>
      
      {/* Trust Badges */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="flex flex-wrap gap-6 mt-12"
      >
        {[
          { icon: Clock, text: "Fast Response" },
          { icon: MapPin, text: "Sudbury & Area" },
          { icon: Shield, text: "Licensed & Insured" }
        ].map((badge, index) => (
          <div key={index} className="flex items-center gap-2 text-white/80">
            <badge.icon className="w-5 h-5 text-[#FACC15]" />
            <span className="text-sm font-medium">{badge.text}</span>
          </div>
        ))}
      </motion.div>
    </div>
  </section>
);

// Services Section
const services = [
  {
    title: "Car Lockout",
    description: "Locked out of your car? We open all makes & models fast without damage.",
    image: "https://images.unsplash.com/photo-1633818807431-14d29e583bcb?crop=entropy&cs=srgb&fm=jpg&q=85",
    tags: ["All Vehicle Types", "No Damage", "24/7 Service"],
    icon: Car,
    size: "large"
  },
  {
    title: "House Lockout",
    description: "Can't get into your home? Our experts get you back inside safely.",
    image: "https://images.unsplash.com/photo-1737046315119-0119468805ab?crop=entropy&cs=srgb&fm=jpg&q=85",
    tags: ["All Lock Types", "24/7 Available", "Same Day Service"],
    icon: Home,
    size: "tall"
  },
  {
    title: "Commercial",
    description: "Secure your business with professional lock installation & repair.",
    image: "https://images.unsplash.com/photo-1615746363486-92cd8c5e0a90?crop=entropy&cs=srgb&fm=jpg&q=85",
    tags: ["Master Keys", "High Security", "Access Control"],
    icon: Building2,
    size: "square"
  },
  {
    title: "Key Services",
    description: "Key cutting, duplication, add-key jobs, and broken key extraction.",
    image: "https://images.pexels.com/photos/8036584/pexels-photo-8036584.jpeg",
    tags: ["Key Cutting", "Add-Key Jobs", "Key Extraction"],
    icon: Key,
    size: "wide"
  }
];

const ServiceCard = ({ service, index }) => {
  const Icon = service.icon;
  return (
    <motion.div
      variants={fadeInUp}
      className={`relative group overflow-hidden bg-[#111111] border border-white/10 card-hover tracing-border
        ${service.size === 'large' ? 'md:col-span-2 md:row-span-2' : ''}
        ${service.size === 'tall' ? 'md:row-span-2' : ''}
        ${service.size === 'wide' ? 'md:col-span-2' : ''}
      `}
      data-testid={`service-card-${service.title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="service-card-image h-48 md:h-full min-h-[200px]">
        <img 
          src={service.image} 
          alt={service.title}
          className="w-full h-full object-cover"
        />
        <div className="image-overlay absolute inset-0" />
      </div>
      
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-[#FACC15] rounded-sm">
            <Icon className="w-5 h-5 text-black" />
          </div>
          <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-white">
            {service.title}
          </h3>
        </div>
        
        <p className="text-[#A1A1AA] mb-4 line-clamp-2">{service.description}</p>
        
        <div className="flex flex-wrap gap-2">
          {service.tags.map((tag, i) => (
            <span key={i} className="text-xs font-bold tracking-wide uppercase text-[#FACC15] bg-[#FACC15]/10 px-3 py-1 rounded-sm">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const ServicesSection = () => (
  <section data-testid="services-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]">
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.span variants={fadeInUp} className="text-sm font-bold tracking-widest uppercase text-[#FACC15] mb-4 block">
          OUR SERVICES
        </motion.span>
        <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-12">
          Full Locksmith Solutions
        </motion.h2>
        
        <motion.p variants={fadeInUp} className="text-[#A1A1AA] text-lg max-w-2xl mb-12">
          From emergency lockouts to complete security upgrades, we've got you covered.
        </motion.p>
        
        <motion.div 
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// Why Choose Us Section
const features = [
  { icon: Zap, title: "Fast Response", description: "We understand emergencies. Our team responds promptly to get you help when you need it." },
  { icon: BadgePercent, title: "Fair Pricing", description: "Honest pricing with no hidden fees. We keep things straightforward." },
  { icon: Clock, title: "24/7 Emergency", description: "Day or night, rain or shine. We're always on call for lockout emergencies in Sudbury." },
  { icon: Shield, title: "Licensed & Insured", description: "Fully certified professionals you can trust in your home or business." },
  { icon: Star, title: "Highly Rated", description: "Trusted by customers throughout Sudbury for reliable locksmith services." },
  { icon: HeartHandshake, title: "Satisfaction Guaranteed", description: "If you're not happy with our work, we'll make it right. Period." }
];

const WhyChooseUsSection = () => (
  <section data-testid="why-choose-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#111111]">
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.span variants={fadeInUp} className="text-sm font-bold tracking-widest uppercase text-[#FACC15] mb-4 block">
          WHY CHOOSE US
        </motion.span>
        <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6">
          Sudbury's Trusted 24/7 Locksmith
        </motion.h2>
        <motion.p variants={fadeInUp} className="text-[#A1A1AA] text-lg max-w-2xl mb-16">
          When you're locked out, you need help fast. We're your local locksmiths in Sudbury, committed to getting you back inside safely.
        </motion.p>
        
        <motion.div 
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {features.map((feature, index) => (
            <motion.div 
              key={index}
              variants={fadeInUp}
              className="p-6 bg-[#1A1A1A] border border-white/10 card-hover"
              data-testid={`feature-${feature.title.toLowerCase().replace(/\s/g, '-')}`}
            >
              <feature.icon className="w-8 h-8 text-[#FACC15] mb-4" />
              <h3 className="font-heading text-xl font-bold text-white mb-2">{feature.title}</h3>
              <p className="text-[#A1A1AA]">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>
        
        {/* Price Guarantee Banner */}
        <motion.div 
          variants={fadeInUp}
          className="mt-12 p-6 bg-[#FACC15]/10 border border-[#FACC15]/30 flex flex-col md:flex-row items-center justify-between gap-4"
        >
          <div className="flex items-center gap-4">
            <BadgePercent className="w-10 h-10 text-[#FACC15]" />
            <div>
              <span className="text-sm font-bold tracking-widest uppercase text-[#FACC15]">FAIR & TRANSPARENT PRICING</span>
              <p className="text-white">No hidden fees. Straightforward and honest service.</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// Google Reviews Section
const googleReviews = [
  {
    text: "I can't thank this locksmith enough for the excellent service they provided. My daughter and I accidentally locked ourselves out of our apartment after stepping out without our keys, and it was a very stressful situation. I called them, and they responded quickly and arrived much sooner than I expected. They were professional, friendly, and reassuring from the moment they arrived. They assessed the situation and had our door open in just a few minutes without causing any damage to the lock or the door. They also took the time to explain what they were doing, which I really appreciated. The pricing was fair, and there were no hidden surprises. What stood out the most was their kindness and willingness to help, especially knowing I was outside with my daughter. Their prompt response and excellent customer service turned a frustrating experience into a positive one. I highly recommend this locksmith to anyone looking for fast, reliable, and professional service. If I ever need a locksmith again, they will definitely be my first call. Thank you so much for your help.",
    author: "Eunice",
    initial: "E"
  },
  {
    text: "Amazing service. Got my keys locked in the car. They provided a very fast and reliable service. I appreciate your help. Will recommend their services.",
    author: "Nataliia Bazarenko",
    initial: "N"
  },
  {
    text: "Locked out late at night and they came quickly! Professional, fast, and fair price. Highly recommend!",
    author: "Mike T.",
    initial: "M"
  },
  {
    text: "Great locksmith service in Sudbury! They were able to open my door without any damage. Will definitely call again.",
    author: "Sarah J.",
    initial: "S"
  },
  {
    text: "Changed all our office locks same day. Great work, reasonable prices, and super professional team.",
    author: "David C.",
    initial: "D"
  },
  {
    text: "Fast response, friendly service, and they even gave me tips on better home security. Thank you!",
    author: "Emily D.",
    initial: "E"
  }
];

const GoogleReviewCard = ({ review }) => (
  <motion.div 
    variants={fadeInUp}
    className="p-6 bg-[#111111] border border-white/10 card-hover"
  >
    {/* Google Review Header */}
    <div className="flex items-start gap-4 mb-4">
      <div className="w-10 h-10 rounded-full bg-[#FACC15] flex items-center justify-center text-black font-bold text-lg flex-shrink-0">
        {review.initial}
      </div>
      <div className="flex-1">
        <span className="text-white font-semibold block">{review.author}</span>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-4 h-4 fill-[#FACC15] text-[#FACC15]" />
            ))}
          </div>
          <svg className="w-4 h-4 ml-2" viewBox="0 0 24 24" fill="none">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
        </div>
      </div>
    </div>
    
    {/* Review Text */}
    <p className="text-[#E4E4E7] leading-relaxed">{review.text}</p>
  </motion.div>
);

const TestimonialsSection = () => (
  <section data-testid="testimonials-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]">
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.span variants={fadeInUp} className="text-sm font-bold tracking-widest uppercase text-[#FACC15] mb-4 block">
          GOOGLE REVIEWS
        </motion.span>
        <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-12">
          What Our Customers Say
        </motion.h2>
        
        <motion.div 
          variants={staggerContainer}
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
        >
          {googleReviews.map((review, index) => (
            <GoogleReviewCard key={index} review={review} />
          ))}
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// Service Area Section with Google Maps
const serviceAreas = ["Sudbury", "Chelmsford", "Lively", "Azilda", "Val Caron", "Hanmer", "Garson", "Capreol"];

const ServiceAreaSection = () => (
  <section data-testid="service-area-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#111111]">
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.span variants={fadeInUp} className="text-sm font-bold tracking-widest uppercase text-[#FACC15] mb-4 block">
          SERVICE AREA
        </motion.span>
        <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6">
          Locksmith Services in Sudbury & Surrounding Areas
        </motion.h2>
        <motion.p variants={fadeInUp} className="text-[#A1A1AA] text-lg max-w-2xl mb-12">
          We proudly serve Greater Sudbury, Ontario and the surrounding communities. Looking for a locksmith near you? We've got you covered.
        </motion.p>
        
        <motion.div 
          variants={fadeInUp}
          className="flex flex-wrap gap-3"
        >
          {serviceAreas.map((area, index) => (
            <span 
              key={index}
              className="px-4 py-2 bg-[#1A1A1A] border border-white/10 text-white font-medium"
            >
              {area}
            </span>
          ))}
        </motion.div>
        
        <motion.p variants={fadeInUp} className="text-[#A1A1AA] mt-8">
          Not sure if we service your area? Give us a call at <a href={PHONE_LINK} onClick={trackCallConversion} className="text-[#FACC15] hover:underline font-semibold">{PHONE_NUMBER}</a> and we'll let you know.
        </motion.p>
      </motion.div>
    </div>
  </section>
);

const AboutSection = () => (
  <section data-testid="about-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#111111]">
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.span variants={fadeInUp} className="text-sm font-bold tracking-widest uppercase text-[#FACC15] mb-4 block">
          ABOUT US
        </motion.span>
        <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6">
          Your Trusted Locksmith in Sudbury
        </motion.h2>
        
        <motion.div variants={fadeInUp} className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-6">
            <p className="text-[#E4E4E7] text-lg leading-relaxed">
              When you're locked out of your car, home, or business in Sudbury, you need a locksmith you can count on — day or night. We provide 24/7 emergency locksmith services throughout Sudbury and surrounding areas, delivering fast, professional assistance when you need it most.
            </p>
            <p className="text-[#A1A1AA] text-base leading-relaxed">
              Our team specializes in residential locksmith services, commercial locksmith solutions, and automotive lockout assistance. Whether you need a car door unlock service because you've locked your keys inside, emergency lockout help at your home, or professional lock services for your business, we're here to help.
            </p>
            <p className="text-[#A1A1AA] text-base leading-relaxed">
              As a local locksmith serving Sudbury, Ontario and the surrounding communities, we understand how stressful a lockout situation can be. That's why we offer prompt response times and reliable service. We also provide key duplication, add-key jobs, and automobile key copying to keep you prepared.
            </p>
          </div>
          
          <div className="space-y-4">
            <div className="p-6 bg-[#1A1A1A] border border-white/10">
              <h3 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-3">
                <Car className="w-6 h-6 text-[#FACC15]" />
                Automotive Services
              </h3>
              <ul className="space-y-2 text-[#A1A1AA]">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#FACC15]" /> Car lockout & door unlock service</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#FACC15]" /> Key cutting & duplication</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#FACC15]" /> Automobile key copying & add-key jobs</li>
              </ul>
            </div>
            
            <div className="p-6 bg-[#1A1A1A] border border-white/10">
              <h3 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-3">
                <Home className="w-6 h-6 text-[#FACC15]" />
                Residential Services
              </h3>
              <ul className="space-y-2 text-[#A1A1AA]">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#FACC15]" /> House lockout assistance</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#FACC15]" /> Lock repair & replacement</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#FACC15]" /> Rekeying services</li>
              </ul>
            </div>
            
            <div className="p-6 bg-[#1A1A1A] border border-white/10">
              <h3 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-3">
                <Building2 className="w-6 h-6 text-[#FACC15]" />
                Commercial Services
              </h3>
              <ul className="space-y-2 text-[#A1A1AA]">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#FACC15]" /> Business lockout service</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#FACC15]" /> Master key systems</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#FACC15]" /> High-security lock installation</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// FAQ Section
const faqData = [
  {
    question: "What locksmith services do you provide in Sudbury?",
    answer: "We provide comprehensive 24/7 locksmith assistance for residential, commercial, and automotive needs throughout Sudbury and surrounding areas. Our services include car lockout help, house lockout assistance, lock repairs, rekeying, key cutting, automobile key copying, add-key jobs, and emergency lockout service for homes and businesses."
  },
  {
    question: "Do you offer 24/7 emergency locksmith service in Sudbury?",
    answer: "Yes, we offer round-the-clock emergency locksmith services in Sudbury, Ontario. Whether it's 2 AM or 2 PM, our team is available 24 hours a day, 7 days a week to help with any urgent lock or lockout situation. Just call 249-979-4550 for immediate assistance."
  },
  {
    question: "Can you unlock my car if I am locked out?",
    answer: "Absolutely. Our car lockout service covers all vehicle makes and models. If you've locked your keys in your car or need car door unlock assistance anywhere in Sudbury or the surrounding area, give us a call. We use professional tools and techniques to get you back into your vehicle without causing damage."
  },
  {
    question: "Do you provide locksmith services near me?",
    answer: "We serve Sudbury and surrounding communities throughout the Greater Sudbury area. If you're searching for a 'locksmith near me' and you're in or around Sudbury, Ontario, we can likely help. Call us at 249-979-4550 to confirm service availability in your specific location."
  },
  {
    question: "How quickly can a locksmith come to me?",
    answer: "Response times depend on several factors including your location, current traffic conditions, weather, and our current service calls. We strive to respond as quickly as possible to all emergency locksmith requests in Sudbury. For the most accurate estimate, please call us directly and we'll give you an honest timeframe."
  },
  {
    question: "Do you provide residential locksmith services?",
    answer: "Yes, we offer a full range of residential locksmith services in Sudbury. This includes house lockout assistance, lock repairs and replacements, rekeying services when you move into a new home or lose your keys, and key duplication. We're here to help keep your home secure."
  },
  {
    question: "Do you provide commercial locksmith services?",
    answer: "Yes, we provide commercial locksmith services for businesses in Sudbury and the surrounding area. Whether you need emergency lockout help, master key systems, high-security lock installation, or general lock maintenance for your business, call us to discuss your specific requirements."
  },
  {
    question: "What areas do you serve?",
    answer: "We serve Sudbury, Ontario and the surrounding areas including Chelmsford, Lively, Azilda, Val Caron, Hanmer, Garson, Capreol, and other nearby communities. If you're unsure whether we cover your location, please call us to confirm."
  },
  {
    question: "How do I contact a locksmith in Sudbury?",
    answer: "For immediate locksmith assistance in Sudbury, call us at 249-979-4550. We're available 24/7 for emergency lockout situations and can help with car lockouts, house lockouts, and commercial lock needs. Don't stay locked out — give us a call and we'll be there to help."
  }
];

const FAQItem = ({ faq, isOpen, onClick }) => (
  <div className="border border-white/10 bg-[#1A1A1A]">
    <button
      onClick={onClick}
      className="w-full p-6 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
      aria-expanded={isOpen}
    >
      <h3 className="font-heading text-lg md:text-xl font-semibold text-white pr-4">{faq.question}</h3>
      <ChevronDown className={`w-5 h-5 text-[#FACC15] flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
      <p className="px-6 pb-6 text-[#A1A1AA] leading-relaxed">{faq.answer}</p>
    </div>
  </div>
);

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);
  
  return (
    <section data-testid="faq-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <span className="text-sm font-bold tracking-widest uppercase text-[#FACC15] mb-4 block">
              FAQ
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
              Common questions about our locksmith services in Sudbury, Ontario.
            </p>
          </motion.div>
          
          <motion.div variants={fadeInUp} className="space-y-3">
            {faqData.map((faq, index) => (
              <FAQItem 
                key={index} 
                faq={faq} 
                isOpen={openIndex === index}
                onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
              />
            ))}
          </motion.div>
          
          {/* CTA within FAQ */}
          <motion.div variants={fadeInUp} className="mt-12 text-center">
            <p className="text-[#A1A1AA] mb-4">Still have questions? We're here to help.</p>
            <Button 
              asChild
              data-testid="faq-call-btn"
              className="bg-[#FACC15] hover:bg-[#EAB308] text-black font-bold text-lg px-8 py-6 rounded-none glow-yellow glow-yellow-hover"
            >
              <a href={PHONE_LINK} onClick={trackCallConversion} className="flex items-center">
                <Phone className="w-5 h-5 mr-2" />
                Call 249-979-4550
              </a>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer CTA Section
const FooterSection = () => (
  <footer data-testid="footer-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0A0A0A] border-t border-white/10">
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
        className="text-center mb-16"
      >
        <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6">
          Need Help Right Now?
        </motion.h2>
        <motion.p variants={fadeInUp} className="text-[#A1A1AA] text-lg mb-10">
          Don't stay locked out. Call us now for immediate assistance.
        </motion.p>
        
        <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row justify-center gap-4">
          <Button 
            asChild
            data-testid="footer-call-btn"
            className="bg-[#FACC15] hover:bg-[#EAB308] text-black font-bold text-xl px-10 py-7 rounded-none glow-yellow glow-yellow-hover"
          >
            <a href={PHONE_LINK} onClick={trackCallConversion} className="flex items-center">
              <Phone className="w-6 h-6 mr-3" />
              {PHONE_NUMBER}
            </a>
          </Button>
          <Button 
            asChild
            data-testid="footer-whatsapp-btn"
            variant="outline" 
            className="bg-white/10 hover:bg-white/20 text-white border-white/20 font-semibold text-lg px-8 py-7 rounded-none backdrop-blur-md"
          >
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" onClick={trackWhatsAppConversion} className="flex items-center">
              <MessageCircle className="w-5 h-5 mr-2" />
              WhatsApp
            </a>
          </Button>
        </motion.div>
      </motion.div>
      
      {/* Contact Info */}
      <motion.div 
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-12 border-t border-white/10"
      >
        <motion.div variants={fadeInUp} className="flex items-center gap-3">
          <Phone className="w-5 h-5 text-[#FACC15]" />
          <a href={PHONE_LINK} onClick={trackCallConversion} className="text-white hover:text-[#FACC15] transition-colors">
            {PHONE_NUMBER}
          </a>
        </motion.div>
        <motion.div variants={fadeInUp} className="flex items-center gap-3">
          <MessageCircle className="w-5 h-5 text-[#FACC15]" />
          <a href={`mailto:${EMAIL}`} className="text-white hover:text-[#FACC15] transition-colors">
            {EMAIL}
          </a>
        </motion.div>
        <motion.div variants={fadeInUp} className="flex items-center gap-3">
          <MapPin className="w-5 h-5 text-[#FACC15]" />
          <span className="text-white">Sudbury, Ontario • 24/7 Emergency</span>
        </motion.div>
      </motion.div>
      
      {/* Copyright */}
      <motion.div 
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4"
      >
        <p className="text-[#A1A1AA] text-sm">
          © {new Date().getFullYear()} {BUSINESS_NAME}. All rights reserved.
        </p>
        <a 
          href="https://app.emergent.sh/?utm_source=emergent-badge" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-[#A1A1AA] text-sm hover:text-white transition-colors"
        >
          Made with Emergent
        </a>
      </motion.div>
    </div>
  </footer>
);

// Mobile Sticky CTA
const MobileStickyCTA = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 500);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!isVisible) return null;

  return (
    <div className="sticky-cta md:hidden" data-testid="mobile-sticky-cta">
      <Button 
        asChild
        className="w-full bg-[#FACC15] hover:bg-[#EAB308] text-black font-bold text-lg py-6 rounded-none glow-yellow"
      >
        <a href={PHONE_LINK} onClick={trackCallConversion} className="flex items-center justify-center">
          <Phone className="w-5 h-5 mr-2" />
          CALL NOW - {PHONE_NUMBER}
        </a>
      </Button>
    </div>
  );
};

// Main Landing Page
export default function LandingPage() {
  return (
    <main>
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <WhyChooseUsSection />
      <TestimonialsSection />
      <FAQSection />
      <ServiceAreaSection />
      <FooterSection />
      <MobileStickyCTA />
    </main>
  );
}
